import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, deleteField } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage, auth } from '../firebase/config';
import { User, Building2, Phone, MapPin, FileText, Save, X, Upload, Image as ImageIcon, CheckCircle, AlertCircle, Trash2, Lock, Shield, ChevronRight, LogOut, Eye, EyeOff, Key, PhoneCall, Wand2, UserMinus, Megaphone, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './common/PageHeader';
import SealGenerator from './SealGenerator';
import { disableNotifications } from '../services/PushNotificationService';

function Profile({ user }) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [bankInfo, setBankInfo] = useState(null); // 정산계좌 — paymentProfiles(비공개)에서 로드 (2026-08-06 보안 P1②)
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '', businessName: '', businessNumber: '', businessPhone: '', address: '', representativeName: '', bizType: '', bizCategory: '', bankName: '', bankAccountNumber: '', bankAccountHolder: '' });
  const [sealImage, setSealImage] = useState(null);
  const [sealPreview, setSealPreview] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  
  const [showPinModal, setShowPinModal] = useState(false);
  const [showSealGenerator, setShowSealGenerator] = useState(false);
  const [pinMode, setPinMode] = useState('');
  const [pinForm, setPinForm] = useState({ currentPin: '', newPin: '', confirmPin: '' });
  const [showPinFields, setShowPinFields] = useState({ current: false, new: false, confirm: false });
  
  // PIN 알림 모달
  const [showPinAlertModal, setShowPinAlertModal] = useState(false);
  
  // 업체 탈퇴 모달
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // 사용자 유형 판단
  const isBusinessOwner = userData && userData.businessType && !userData.companyId;
  const isStaff = userData && userData.companyId;
  const isIndividual = userData && !userData.businessType && !userData.companyId;

  useEffect(() => { loadUserData(); }, [user]);
  useEffect(() => { if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 3000); return () => clearTimeout(t); } }, [successMessage]);
  useEffect(() => { if (errorMessage) { const t = setTimeout(() => setErrorMessage(''), 3000); return () => clearTimeout(t); } }, [errorMessage]);

  // PIN 미설정 알림 (업체 대표만)
  useEffect(() => {
    if (isBusinessOwner && userData && !(userData.pin || userData.pinSet) && !loading) {
      const dismissed = sessionStorage.getItem(`pin_alert_dismissed_${user.uid}`);
      if (!dismissed) {
        setShowPinAlertModal(true);
      }
    }
  }, [isBusinessOwner, userData, loading, user]);

  const loadUserData = async () => {
    if (!user) return;
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (userSnap.exists()) {
        const data = userSnap.data();
        // 계좌는 paymentProfiles(비공개)에 보관 — users 공개 문서에서 이전 (2026-08-06 보안 P1②)
        let bank = null;
        try {
          const payProf = await getDoc(doc(db, 'paymentProfiles', user.uid));
          bank = payProf.exists() ? (payProf.data().domesticBank || null) : null;
        } catch (e) { console.error('정산계좌 조회 실패:', e); }
        if (!bank && data.bankAccount) bank = data.bankAccount; // 마이그레이션 전 레거시 폴백
        setBankInfo(bank);
        setUserData(data);
        setEditData({
          name: data.name || '', 
          phone: data.phone || '', 
          businessName: data.businessName || '', 
          businessNumber: data.businessNumber || '', 
          businessPhone: data.businessPhone || '',
          address: data.address || '',
          representativeName: data.representativeName || '',
          bizType: data.bizType || '',
          bizCategory: data.bizCategory || '',
          bankName: bank?.bankName || '',
          bankAccountNumber: bank?.accountNumber || '',
          bankAccountHolder: bank?.accountHolder || data.representativeName || '',
        });
        setSealPreview(data.sealImageUrl || null);
        
        // 소속 업체 정보 로드
        if (data.companyId) {
          const companySnap = await getDoc(doc(db, 'users', data.companyId));
          if (companySnap.exists()) {
            setCompanyData({ id: companySnap.id, ...companySnap.data() });
          }
        }
      }
    } catch (error) { showError('데이터를 불러오는데 실패했습니다.'); }
    finally { setLoading(false); }
  };

  const showSuccess = (msg) => { setSuccessMessage(msg); setErrorMessage(''); };
  const showError = (msg) => { setErrorMessage(msg); setSuccessMessage(''); };

  const getBusinessTypeLabel = () => {
    const type = userData?.businessType;
    if (type === 'dental' || type === 'clinic') return '치과';
    if (type === 'lab') return '기공소';
    return '-';
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      let sealImageUrl = userData?.sealImageUrl || null;
      if (sealImage && isBusinessOwner) {
        const sealRef = ref(storage, `seals/${user.uid}/${Date.now()}_${sealImage.name}`);
        await uploadBytes(sealRef, sealImage);
        sealImageUrl = await getDownloadURL(sealRef);
        setSealImage(null);
      }
      
      const updateData = { 
        name: editData.name,
        phone: editData.phone,
        updatedAt: new Date().toISOString() 
      };
      
      if (isBusinessOwner) {
        updateData.businessName = editData.businessName;
        updateData.businessNumber = editData.businessNumber;
        updateData.businessPhone = editData.businessPhone;
        updateData.address = editData.address;
        updateData.representativeName = editData.representativeName;
        updateData.bizType = editData.bizType;
        updateData.bizCategory = editData.bizCategory;
        // 계좌는 paymentProfiles(비공개)에 저장 — users에는 두지 않는다 (2026-08-06 보안 P1②)
        const newBank = {
            bankName: (editData.bankName || '').trim(),
            accountNumber: (editData.bankAccountNumber || '').trim(),
            accountHolder: (editData.bankAccountHolder || '').trim(),
        };
        await setDoc(doc(db, 'paymentProfiles', user.uid), {
            domesticBank: newBank,
            updatedAt: new Date(),
        }, { merge: true });
        setBankInfo(newBank);
        updateData.bankAccount = deleteField(); // 레거시 공개 필드 정리
        updateData.sealImageUrl = sealImageUrl;
      }

      await updateDoc(doc(db, 'users', user.uid), updateData);
      await loadUserData();
      setEditMode(false);
      showSuccess('프로필이 저장되었습니다.');
    } catch (error) { showError('프로필 저장에 실패했습니다.'); }
    finally { setSaving(false); }
  };

  const handleSealImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSealImage(file); const reader = new FileReader(); reader.onloadend = () => setSealPreview(reader.result); reader.readAsDataURL(file); }
  };

  const handleDeleteSealImage = async () => {
    if (!window.confirm('인감 이미지를 삭제하시겠습니까?')) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, 'users', user.uid), { sealImageUrl: null, updatedAt: new Date().toISOString() });
      await loadUserData();
      setSealPreview(null);
      showSuccess('인감 이미지가 삭제되었습니다.');
    } catch (error) { showError('인감 이미지 삭제에 실패했습니다.'); }
    finally { setSaving(false); }
  };

  const handleSealGenerated = async (file, dataUrl) => {
    try {
      setSaving(true);
      const sealRef = ref(storage, `seals/${user.uid}/${Date.now()}_generated.png`);
      await uploadBytes(sealRef, file);
      const sealImageUrl = await getDownloadURL(sealRef);
      await updateDoc(doc(db, 'users', user.uid), { sealImageUrl, updatedAt: new Date().toISOString() });
      setSealPreview(dataUrl);
      setShowSealGenerator(false);
      showSuccess('인감이 생성되어 저장되었습니다.');
      await loadUserData();
    } catch (error) {
      showError('인감 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { showError('새 비밀번호가 일치하지 않습니다.'); return; }
    if (passwordForm.newPassword.length < 6) { showError('비밀번호는 최소 6자 이상이어야 합니다.'); return; }
    try {
      setSaving(true);
      const credential = EmailAuthProvider.credential(auth.currentUser.email, passwordForm.currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwordForm.newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      showSuccess('비밀번호가 변경되었습니다.');
    } catch (error) {
      if (error.code === 'auth/wrong-password') showError('현재 비밀번호가 올바르지 않습니다.');
      else showError('비밀번호 변경에 실패했습니다.');
    } finally { setSaving(false); }
  };

  const startPinSetup = (mode) => {
    setPinMode(mode);
    setShowPinModal(true);
    setPinForm({ currentPin: '', newPin: '', confirmPin: '' });
    setShowPinFields({ current: false, new: false, confirm: false });
  };

  // 2026-07-30: 핀은 서버(Cloud Function)에만 저장/검증 — 브라우저에 평문 저장 금지 (회의 확정)
  const handlePinAction = async () => {
    const pinOk = (v) => /^\d{4,6}$/.test(v || '');
    const callSetPin = async (payload) => {
      const fns = getFunctions(undefined, 'asia-northeast3');
      await httpsCallable(fns, 'setOwnerPin')(payload);
    };
    if (pinMode === 'set') {
      if (!pinOk(pinForm.newPin)) { showError('PIN은 4~6자리 숫자로 입력해주세요.'); return; }
      if (pinForm.newPin !== pinForm.confirmPin) { showError('PIN이 일치하지 않습니다.'); return; }
      try {
        setSaving(true);
        await callSetPin({ newPin: pinForm.newPin });
        await loadUserData();
        setShowPinModal(false);
        showSuccess('PIN이 설정되었습니다.');
      } catch (error) { showError(error?.message || 'PIN 설정에 실패했습니다.'); }
      finally { setSaving(false); }
    } else if (pinMode === 'change') {
      if (!pinOk(pinForm.newPin)) { showError('PIN은 4~6자리 숫자로 입력해주세요.'); return; }
      if (pinForm.newPin !== pinForm.confirmPin) { showError('새 PIN이 일치하지 않습니다.'); return; }
      try {
        setSaving(true);
        await callSetPin({ currentPin: pinForm.currentPin, newPin: pinForm.newPin });
        await loadUserData();
        setShowPinModal(false);
        showSuccess('PIN이 변경되었습니다.');
      } catch (error) { showError(error?.message || '현재 PIN이 올바르지 않습니다.'); }
      finally { setSaving(false); }
    } else if (pinMode === 'remove') {
      if (!window.confirm('PIN을 삭제하시겠습니까?\n대표 메뉴 잠금이 사라집니다.')) return;
      try {
        setSaving(true);
        await callSetPin({ currentPin: pinForm.currentPin, remove: true });
        await loadUserData();
        setShowPinModal(false);
        showSuccess('PIN이 삭제되었습니다.');
      } catch (error) { showError(error?.message || '현재 PIN이 올바르지 않습니다.'); }
      finally { setSaving(false); }
    }
  };

  const dismissPinAlert = () => {
    sessionStorage.setItem(`pin_alert_dismissed_${user.uid}`, 'true');
    setShowPinAlertModal(false);
  };

  const goToPinSetup = () => {
    setShowPinAlertModal(false);
    sessionStorage.setItem(`pin_alert_dismissed_${user.uid}`, 'true');
    startPinSetup('set');
  };

  // 업체 탈퇴 → 개인회원으로 전환
  const handleLeaveCompany = async () => {
    if (!window.confirm(`${companyData?.businessName || '업체'}에서 탈퇴하시겠습니까?\n탈퇴 후 개인회원으로 전환됩니다.`)) return;
    // 2026-10-12 A-7: 종전 users 직접 쓰기(companyId 등)는 rules 금지 필드라 거부됐다 →
    //   서버 callable leaveCompany(입력 없음 — 항상 호출자 본인, StaffAffiliation.js 와 동일)로 교체.
    try {
      setSaving(true);
      const leave = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'leaveCompany');
      await leave({});
      setCompanyData(null);
      await loadUserData();
      setShowCompanyModal(false);
      showSuccess('업체에서 탈퇴되었습니다. 개인회원으로 전환되었습니다.');
      // 페이지 새로고침으로 메뉴 갱신
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      // 조용한 실패 금지 — 서버가 준 사유(대표 계정·소속 없음 등)를 그대로 보여 준다
      console.error('업체 탈퇴 실패:', error?.code || '', error?.message || error);
      const serverMsg = (error?.code && error.code !== 'functions/internal' && error?.message) ? error.message : '';
      showError(serverMsg || '탈퇴에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      try { await disableNotifications(user?.uid); } catch (e) { console.log('알림 비활성화 실패:', e); }
      auth.signOut();
    }
  };

  if (loading) return (
    <div style={styles.container}>
      <div style={styles.loadingContainer}><div style={styles.spinner}></div><p style={styles.loadingText}>로딩 중...</p></div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={styles.container}>
      <PageHeader icon={User} title="프로필" />
      {successMessage && <div style={styles.successMsg}><CheckCircle size={18} /> {successMessage}</div>}
      {errorMessage && <div style={styles.errorMsg}><AlertCircle size={18} /> {errorMessage}</div>}

      {userData && (
        <>
          {/* 기본 정보 */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={{...styles.sectionIconBox, backgroundColor: '#3b82f6'}}><User size={18} color="#fff" /></div>
              <span style={styles.sectionTitle}>기본 정보</span>
              {!editMode && <button onClick={() => setEditMode(true)} style={styles.editIconBtn}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>}
            </div>
            <div style={styles.cardBody}>
              {editMode ? (
                <>
                  <div style={styles.inputGroup}><label style={styles.inputLabel}>이름 *</label><div style={styles.inputBox}><User size={18} color="#64748b" /><input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} placeholder="이름" style={styles.input} /></div></div>
                  <div style={styles.inputGroup}><label style={styles.inputLabel}>전화번호</label><div style={styles.inputBox}><Phone size={18} color="#64748b" /><input type="tel" value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} placeholder="전화번호" style={styles.input} /></div></div>
                  {isBusinessOwner && (
                    <>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>업체명</label><div style={styles.inputBox}><Building2 size={18} color="#64748b" /><input type="text" value={editData.businessName} onChange={(e) => setEditData({...editData, businessName: e.target.value})} placeholder="업체명" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>대표전화번호</label><div style={styles.inputBox}><PhoneCall size={18} color="#64748b" /><input type="tel" value={editData.businessPhone} onChange={(e) => setEditData({...editData, businessPhone: e.target.value})} placeholder="대표전화번호" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>사업자번호</label><div style={styles.inputBox}><FileText size={18} color="#64748b" /><input type="text" value={editData.businessNumber} onChange={(e) => setEditData({...editData, businessNumber: e.target.value})} placeholder="사업자번호" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>주소</label><div style={styles.inputBox}><MapPin size={18} color="#64748b" /><input type="text" value={editData.address} onChange={(e) => setEditData({...editData, address: e.target.value})} placeholder="주소" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>대표자명</label><div style={styles.inputBox}><User size={18} color="#64748b" /><input type="text" value={editData.representativeName} onChange={(e) => setEditData({...editData, representativeName: e.target.value})} placeholder="대표자명" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>업태</label><div style={styles.inputBox}><FileText size={18} color="#64748b" /><input type="text" value={editData.bizType} onChange={(e) => setEditData({...editData, bizType: e.target.value})} placeholder="업태 (예: 제조업)" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>종목</label><div style={styles.inputBox}><FileText size={18} color="#64748b" /><input type="text" value={editData.bizCategory} onChange={(e) => setEditData({...editData, bizCategory: e.target.value})} placeholder="종목 (예: 치과기공)" style={styles.input} /></div></div>
                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '8px 0', paddingTop: 8, fontSize: 12, color: '#64748b', fontWeight: 600 }}>이체받을 계좌 (거래명세서에 표시됩니다)</div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>은행명</label><div style={styles.inputBox}><CreditCard size={18} color="#64748b" /><input type="text" value={editData.bankName} onChange={(e) => setEditData({...editData, bankName: e.target.value})} placeholder="예: 국민, 신한, 우리" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>계좌번호</label><div style={styles.inputBox}><CreditCard size={18} color="#64748b" /><input type="text" value={editData.bankAccountNumber} onChange={(e) => setEditData({...editData, bankAccountNumber: e.target.value})} placeholder="123-456-789012" style={styles.input} /></div></div>
                      <div style={styles.inputGroup}><label style={styles.inputLabel}>예금주</label><div style={styles.inputBox}><User size={18} color="#64748b" /><input type="text" value={editData.bankAccountHolder} onChange={(e) => setEditData({...editData, bankAccountHolder: e.target.value})} placeholder="예금주명" style={styles.input} /></div></div>
                    </>
                  )}
                  <div style={styles.buttonRow}><button style={styles.cancelButton} onClick={() => { setEditMode(false); loadUserData(); }}>취소</button><button style={styles.saveButton} onClick={handleSaveProfile} disabled={saving}>{saving ? '저장 중...' : '저장'}</button></div>
                </>
              ) : (
                <>
                  <div style={styles.infoRow}><span style={styles.infoLabel}>이름</span><span style={styles.infoValue}>{userData.name || '-'}</span></div>
                  <div style={styles.infoRow}><span style={styles.infoLabel}>전화번호</span><span style={styles.infoValue}>{userData.phone || '-'}</span></div>
                  <div style={styles.infoRow}><span style={styles.infoLabel}>이메일</span><span style={styles.infoValue}>{user?.email}</span></div>
                  {isBusinessOwner && (
                    <>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>업체명</span><span style={styles.infoValue}>{userData.businessName || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>대표전화</span><span style={styles.infoValue}>{userData.businessPhone || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>사업자번호</span><span style={styles.infoValue}>{userData.businessNumber || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>업체 유형</span><span style={styles.infoValue}>{getBusinessTypeLabel()}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>주소</span><span style={styles.infoValue}>{userData.address || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>대표자명</span><span style={styles.infoValue}>{userData.representativeName || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>업태</span><span style={styles.infoValue}>{userData.bizType || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>종목</span><span style={styles.infoValue}>{userData.bizCategory || '-'}</span></div>
                      <div style={styles.infoRow}><span style={styles.infoLabel}>이체받을 계좌</span>
                        <span style={styles.infoValue}>
                          {bankInfo?.accountNumber
                            ? `${bankInfo.bankName || ''} ${bankInfo.accountNumber}${bankInfo.accountHolder ? ` (예금주: ${bankInfo.accountHolder})` : ''}`
                            : '-'}
                        </span>
                      </div>
                    </>
                  )}
                  {isStaff && companyData && (
                    <div style={styles.infoRow}><span style={styles.infoLabel}>소속 업체</span><span style={styles.infoValue}>{companyData.businessName || '-'}</span></div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 인감 이미지 - 업체 대표만 */}
          {isBusinessOwner && (
            <div style={styles.card}>
              <div style={styles.sectionHeader}><div style={{...styles.sectionIconBox, backgroundColor: '#8b5cf6'}}><ImageIcon size={18} color="#fff" /></div><span style={styles.sectionTitle}>인감 이미지</span></div>
              <div style={styles.cardBody}>
                <div style={styles.imageSection}>
                  {(sealPreview || userData.sealImageUrl) ? (
                    <div style={styles.sealContainer}>
                      <img src={sealPreview || userData.sealImageUrl} alt="인감" style={styles.sealImage} />
                      <div style={styles.imageActions}>
                        <label style={styles.changeSealButton}><Upload size={14} /> 변경<input type="file" accept="image/*" onChange={handleSealImageChange} style={{display:'none'}} /></label>
                        {sealImage && <button onClick={handleSaveProfile} disabled={saving} style={styles.saveSmallBtn}><Save size={14} /> 저장</button>}
                        {!sealImage && <button onClick={handleDeleteSealImage} disabled={saving} style={styles.deleteSmallBtn}><Trash2 size={14} /> 삭제</button>}
                      </div>
                      <button onClick={() => setShowSealGenerator(true)} style={styles.generateBtn}><Wand2 size={16} /> 새 인감 만들기</button>
                    </div>
                  ) : (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center'}}>
                      <label style={styles.uploadButton}><Upload size={22} color="#6366f1" /><span>인감 이미지 업로드</span><input type="file" accept="image/*" onChange={handleSealImageChange} style={{display:'none'}} /></label>
                      <button onClick={() => setShowSealGenerator(true)} style={{...styles.uploadButton, backgroundColor: '#f0fdf4', cursor: 'pointer', border: 'none'}}><Wand2 size={22} color="#16a34a" /><span style={{color: '#16a34a'}}>인감 만들기</span></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 보안 - 업체 대표만 PIN 표시 */}
          {isBusinessOwner && (
            <div style={styles.card}>
              <div style={styles.sectionHeader}><div style={{...styles.sectionIconBox, backgroundColor: '#10b981'}}><Shield size={18} color="#fff" /></div><span style={styles.sectionTitle}>보안</span></div>
              <div style={styles.cardBody}>
                <div style={styles.securityItem} onClick={() => setShowPasswordModal(true)}><div style={styles.securityItemLeft}><Lock size={18} color="#64748b" /><span>비밀번호 변경</span></div><ChevronRight size={18} color="#cbd5e1" /></div>
                <div style={styles.securityItem} onClick={() => startPinSetup((userData?.pin || userData?.pinSet) ? 'change' : 'set')}><div style={styles.securityItemLeft}><Key size={18} color="#64748b" /><span>PIN {(userData?.pin || userData?.pinSet) ? '변경' : '설정'}</span></div><div style={styles.securityItemRight}>{(userData?.pin || userData?.pinSet) && <div style={styles.pinBadge}><CheckCircle size={12} color="#10b981" /><span>설정됨</span></div>}<ChevronRight size={18} color="#cbd5e1" /></div></div>
                {(userData?.pin || userData?.pinSet) && <div style={styles.securityItem} onClick={() => startPinSetup('remove')}><div style={styles.securityItemLeft}><Trash2 size={18} color="#ef4444" /><span style={{color:'#ef4444'}}>PIN 삭제</span></div><ChevronRight size={18} color="#cbd5e1" /></div>}
              </div>
            </div>
          )}

          {/* 보안 - 개인/직원은 비밀번호만 */}
          {(isIndividual || isStaff) && (
            <div style={styles.card}>
              <div style={styles.sectionHeader}><div style={{...styles.sectionIconBox, backgroundColor: '#10b981'}}><Shield size={18} color="#fff" /></div><span style={styles.sectionTitle}>보안</span></div>
              <div style={styles.cardBody}>
                <div style={styles.securityItem} onClick={() => setShowPasswordModal(true)}><div style={styles.securityItemLeft}><Lock size={18} color="#64748b" /><span>비밀번호 변경</span></div><ChevronRight size={18} color="#cbd5e1" /></div>
              </div>
            </div>
          )}

          {/* 업체 탈퇴 - 직원만 */}
          {isStaff && companyData && (
            <div style={styles.card}>
              <div style={styles.sectionHeader}><div style={{...styles.sectionIconBox, backgroundColor: '#f59e0b'}}><Building2 size={18} color="#fff" /></div><span style={styles.sectionTitle}>소속 업체</span></div>
              <div style={styles.cardBody}>
                <div style={styles.companyInfoCard}>
                  <Building2 size={32} color="#6366f1" />
                  <div style={{flex: 1}}>
                    <p style={{fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0'}}>{companyData.businessName}</p>
                    <p style={{fontSize: '13px', color: '#64748b', margin: 0}}>{companyData.businessType === 'dental' || companyData.businessType === 'clinic' ? '치과' : '기공소'}</p>
                  </div>
                </div>
                <div style={styles.securityItem} onClick={() => setShowCompanyModal(true)}>
                  <div style={styles.securityItemLeft}><UserMinus size={18} color="#ef4444" /><span style={{color:'#ef4444'}}>업체 탈퇴</span></div>
                  <ChevronRight size={18} color="#cbd5e1" />
                </div>
              </div>
            </div>
          )}

          {/* 로그아웃 */}
          <button style={styles.logoutButton} onClick={handleLogout}><LogOut size={18} color="#ef4444" /><span>로그아웃</span></button>
        </>
      )}

      {/* PIN 알림 모달 */}
      {showPinAlertModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.alertModalContent}>
            <div style={styles.alertIcon}><Key size={48} color="#f59e0b" /></div>
            <h3 style={styles.alertTitle}>PIN 설정 안내</h3>
            <p style={styles.alertText}>
              보안 강화를 위해 PIN 번호를 설정해주세요.<br/>
              중요한 작업 시 PIN 인증이 필요합니다.
            </p>
            <div style={styles.alertButtons}>
              <button onClick={dismissPinAlert} style={styles.alertSecondaryBtn}>나중에</button>
              <button onClick={goToPinSetup} style={styles.alertPrimaryBtn}>PIN 설정하기</button>
            </div>
          </div>
        </div>
      )}

      {/* 업체 탈퇴 확인 모달 */}
      {showCompanyModal && companyData && (
        <div style={styles.modalOverlay} onClick={() => setShowCompanyModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={styles.modalTitle}>업체 탈퇴</h3><button onClick={() => setShowCompanyModal(false)} style={styles.closeBtn}><X size={24} color="#64748b" /></button></div>
            <div style={styles.modalBody}>
              <div style={styles.companyInfo}>
                <Building2 size={40} color="#6366f1" />
                <div>
                  <p style={{fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0'}}>{companyData.businessName}</p>
                  <p style={{fontSize: '14px', color: '#64748b', margin: 0}}>{companyData.businessType === 'dental' || companyData.businessType === 'clinic' ? '치과' : '기공소'}</p>
                </div>
              </div>
              <div style={styles.warningBox}>
                <AlertCircle size={20} color="#f59e0b" />
                <p style={{margin: 0, fontSize: '14px', color: '#92400e'}}>
                  탈퇴 후 <strong>개인회원</strong>으로 전환됩니다.<br/>
                  업체 관련 기능을 사용할 수 없습니다.
                </p>
              </div>
              <button style={{...styles.submitButton, backgroundColor: '#ef4444'}} onClick={handleLeaveCompany} disabled={saving}>
                {saving ? '처리 중...' : '업체 탈퇴하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 비밀번호 변경 모달 */}
      {showPasswordModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={styles.modalTitle}>비밀번호 변경</h3><button onClick={() => setShowPasswordModal(false)} style={styles.closeBtn}><X size={24} color="#64748b" /></button></div>
            <div style={styles.modalBody}>
              <div style={styles.inputGroup}><label style={styles.inputLabel}>현재 비밀번호</label><div style={styles.inputBox}><Lock size={18} color="#64748b" /><input type={showPasswords.current ? 'text' : 'password'} value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})} placeholder="현재 비밀번호" style={styles.input} /><button onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})} style={styles.eyeBtn}>{showPasswords.current ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>
              <div style={styles.inputGroup}><label style={styles.inputLabel}>새 비밀번호</label><div style={styles.inputBox}><Lock size={18} color="#64748b" /><input type={showPasswords.new ? 'text' : 'password'} value={passwordForm.newPassword} onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})} placeholder="새 비밀번호" style={styles.input} /><button onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})} style={styles.eyeBtn}>{showPasswords.new ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>
              <div style={styles.inputGroup}><label style={styles.inputLabel}>새 비밀번호 확인</label><div style={styles.inputBox}><Lock size={18} color="#64748b" /><input type={showPasswords.confirm ? 'text' : 'password'} value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} placeholder="새 비밀번호 확인" style={styles.input} /><button onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})} style={styles.eyeBtn}>{showPasswords.confirm ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>
              <button style={styles.submitButton} onClick={handlePasswordChange} disabled={saving}>{saving ? '변경 중...' : '비밀번호 변경'}</button>
            </div>
          </div>
        </div>
      )}

      {/* PIN 관리 모달 */}
      {showPinModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPinModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={styles.modalTitle}>PIN {pinMode === 'set' ? '설정' : pinMode === 'change' ? '변경' : '삭제'}</h3><button onClick={() => setShowPinModal(false)} style={styles.closeBtn}><X size={24} color="#64748b" /></button></div>
            <div style={styles.modalBody}>
              {(pinMode === 'change' || pinMode === 'remove') && <div style={styles.inputGroup}><label style={styles.inputLabel}>현재 PIN</label><div style={styles.inputBox}><Key size={18} color="#64748b" /><input type={showPinFields.current ? 'text' : 'password'} value={pinForm.currentPin} onChange={(e) => setPinForm({...pinForm, currentPin: e.target.value.replace(/\D/g,'').slice(0,6)})} placeholder="현재 PIN" maxLength={6} style={styles.input} /><button onClick={() => setShowPinFields({...showPinFields, current: !showPinFields.current})} style={styles.eyeBtn}>{showPinFields.current ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>}
              {pinMode !== 'remove' && (
                <>
                  <div style={styles.inputGroup}><label style={styles.inputLabel}>새 PIN</label><div style={styles.inputBox}><Key size={18} color="#64748b" /><input type={showPinFields.new ? 'text' : 'password'} value={pinForm.newPin} onChange={(e) => setPinForm({...pinForm, newPin: e.target.value.replace(/\D/g,'').slice(0,6)})} placeholder="새 PIN (4~6자리 숫자)" maxLength={6} style={styles.input} /><button onClick={() => setShowPinFields({...showPinFields, new: !showPinFields.new})} style={styles.eyeBtn}>{showPinFields.new ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>
                  <div style={styles.inputGroup}><label style={styles.inputLabel}>새 PIN 확인</label><div style={styles.inputBox}><Key size={18} color="#64748b" /><input type={showPinFields.confirm ? 'text' : 'password'} value={pinForm.confirmPin} onChange={(e) => setPinForm({...pinForm, confirmPin: e.target.value.replace(/\D/g,'').slice(0,6)})} placeholder="새 PIN 확인" maxLength={6} style={styles.input} /><button onClick={() => setShowPinFields({...showPinFields, confirm: !showPinFields.confirm})} style={styles.eyeBtn}>{showPinFields.confirm ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}</button></div></div>
                </>
              )}
              <button style={{...styles.submitButton, backgroundColor: pinMode === 'remove' ? '#ef4444' : '#6366f1'}} onClick={handlePinAction} disabled={saving}>{saving ? '처리 중...' : pinMode === 'set' ? 'PIN 설정' : pinMode === 'change' ? 'PIN 변경' : 'PIN 삭제'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 인감 생성기 모달 */}
      {showSealGenerator && (
        <SealGenerator 
          onSave={handleSealGenerated}
          onClose={() => setShowSealGenerator(false)}
          defaultText={userData?.businessName || userData?.name || ''}
        />
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '24px', maxWidth: '800px', margin: '0 auto' },
  card: { backgroundColor: '#fff', borderRadius: '16px', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f1f5f9', gap: '14px' },
  sectionIconBox: { width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#0f172a', flex: 1 },
  cardBody: { padding: '24px' },
  editIconBtn: { width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  inputGroup: { marginBottom: '20px' },
  inputLabel: { display: 'block', fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '10px' },
  inputBox: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '0 16px', height: '56px', gap: '12px' },
  input: { flex: 1, fontSize: '16px', color: '#0f172a', border: 'none', background: 'transparent', outline: 'none' },
  buttonRow: { display: 'flex', gap: '12px', marginTop: '12px' },
  cancelButton: { flex: 1, padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#fff', fontSize: '16px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  saveButton: { flex: 1, padding: '16px', borderRadius: '12px', border: 'none', backgroundColor: '#6366f1', fontSize: '16px', fontWeight: '600', color: '#fff', cursor: 'pointer' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f1f5f9' },
  infoLabel: { fontSize: '15px', fontWeight: '600', color: '#64748b' },
  infoValue: { fontSize: '16px', color: '#0f172a', fontWeight: '500' },
  imageSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' },
  sealContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  sealImage: { width: '180px', height: '180px', borderRadius: '14px', border: '1px solid #e2e8f0', objectFit: 'contain', backgroundColor: '#fff' },
  imageActions: { display: 'flex', gap: '10px' },
  changeSealButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#f1f5f9', borderRadius: '10px', fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer', border: 'none' },
  saveSmallBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#6366f1', borderRadius: '10px', fontSize: '14px', fontWeight: '500', color: '#fff', cursor: 'pointer', border: 'none' },
  deleteSmallBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#fee2e2', borderRadius: '10px', fontSize: '14px', fontWeight: '500', color: '#ef4444', cursor: 'pointer', border: 'none' },
  generateBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#16a34a', cursor: 'pointer' },
  uploadButton: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px', backgroundColor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: '14px', cursor: 'pointer', fontSize: '15px', fontWeight: '500', color: '#6366f1', width: '100%', maxWidth: '280px' },
  securityItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
  securityItemLeft: { display: 'flex', alignItems: 'center', gap: '14px', fontSize: '16px', color: '#374151' },
  securityItemRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  pinBadge: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#dcfce7', borderRadius: '20px', fontSize: '13px', color: '#16a34a', fontWeight: '500' },
  logoutButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%', padding: '18px', backgroundColor: '#fff', border: '1px solid #fecaca', borderRadius: '14px', fontSize: '16px', fontWeight: '600', color: '#ef4444', cursor: 'pointer', marginTop: '10px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '20px', width: '100%', maxWidth: '440px', overflow: 'hidden' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f1f5f9' },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 },
  closeBtn: { width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#f8fafc', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  modalBody: { padding: '24px' },
  eyeBtn: { backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' },
  submitButton: { width: '100%', padding: '18px', backgroundColor: '#6366f1', borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: '600', color: '#fff', cursor: 'pointer', marginTop: '10px' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner: { width: '50px', height: '50px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { marginTop: '16px', fontSize: '16px', color: '#64748b' },
  successMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', backgroundColor: '#dcfce7', borderRadius: '12px', marginBottom: '20px', fontSize: '15px', fontWeight: '500', color: '#166534' },
  errorMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', backgroundColor: '#fee2e2', borderRadius: '12px', marginBottom: '20px', fontSize: '15px', fontWeight: '500', color: '#991b1b' },
  alertModalContent: { backgroundColor: '#fff', borderRadius: '20px', width: '100%', maxWidth: '360px', padding: '32px 24px', textAlign: 'center' },
  alertIcon: { width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  alertTitle: { fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0' },
  alertText: { fontSize: '15px', color: '#64748b', lineHeight: 1.6, margin: '0 0 24px 0' },
  alertButtons: { display: 'flex', gap: '12px' },
  alertSecondaryBtn: { flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  alertPrimaryBtn: { flex: 1, padding: '14px', borderRadius: '12px', border: 'none', backgroundColor: '#6366f1', fontSize: '15px', fontWeight: '600', color: '#fff', cursor: 'pointer' },
  companyInfo: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', marginBottom: '20px' },
  companyInfoCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', marginBottom: '16px' },
  warningBox: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '12px', marginBottom: '20px' },
  toggleContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  toggleInfo: { display: 'flex', alignItems: 'center', gap: '14px' },
  toggleTextBox: { display: 'flex', flexDirection: 'column', gap: '2px' },
  toggleTitle: { fontSize: '16px', fontWeight: '600', color: '#0f172a' },
  toggleDesc: { fontSize: '13px', color: '#64748b' },
  toggleButton: { width: '56px', height: '32px', borderRadius: '16px', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s' },
  toggleKnob: { width: '28px', height: '28px', borderRadius: '14px', backgroundColor: '#fff', position: 'absolute', top: '2px', left: '2px', transition: 'transform 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }
};

export default Profile;